import { z } from 'zod/v4';

import { paymentViewSchema } from '@sites/entity-core';

import { formAppearanceShape } from '../form-shared/partial-form-appearance.schema';

/** A price has to be a real one — the guard is against a sign, not against a magnitude. */
const AMOUNT_MIN = 0.01;

/**
 * THE payment-form data shape as AI tools accept it.
 *
 * Most of it is `formAppearanceShape`, shared with the subscription form because the two store the
 * same four groups from the same entity-core schemas (`payment-form/schemas/legacy/data.ts` imports
 * them from the form widget outright). What is here is this widget's own: the visible headings, the
 * price, and the modal hint.
 *
 * **No `create_payment_form_widget` exists, and this schema is why it can't yet.** A payment form's
 * substance is `paymentElements[0]`, whose `type` is one of the account's actually-connected payment
 * methods — the editor fills it from `injectPaymentMethodsQuery` via
 * `createDefaultPaymentElementLegacy`. The factory default is `{ type: 0 (PayPal), amount: 0,
 * payment_method_id: null }`, and the published form reads BOTH fields through truthiness
 * (`if (this.paymentElement.payment_method_id)` … `if (this.paymentElement.type)`) — where `0` is
 * falsy. So a form created from the defaults submits a payment request naming no method at all: it
 * renders, it takes input, and it cannot charge anyone. Creating one becomes possible with the tool
 * that lists the account's real payment methods (§3.6 stage A of `ai-mcp-plan.md`).
 *
 * Absent for the same reason as on the subscription form: `selectedAddressBook`, the CRM pipeline
 * trio, the tag lists. Absent additionally: `productType: 'eduCourse'` with its `courseId`/
 * `courseTariffId` (external ids plus EDU specifics), and `rro_info` (Ukrainian fiscalisation),
 * where a wrong value is a tax document.
 */
export const partialPaymentFormWidgetDataSchema = z.object({
    ...formAppearanceShape,

    // --- what the visitor reads ---
    paymentFormName: z
        .string()
        .min(1)
        .optional()
        .describe('Heading above the form — visible on the published page, not an internal name.'),
    productName: z
        .string()
        .min(1)
        .optional()
        .describe('What is being sold, visible on the page and sent to the payment provider as the ' + 'order name.'),

    // --- price ---
    amount: z
        .number()
        .min(AMOUNT_MIN)
        .optional()
        .describe(
            'Price. Also the lower bound when the visitor is allowed to enter their own amount, so a ' +
                'zero would let anyone pay nothing.'
        ),
    currency: z
        .string()
        .length(3)
        .optional()
        .describe('Three-letter currency code, e.g. "USD". Must be one the connected payment method supports.'),

    // --- view mode ---
    formView: paymentViewSchema
        .optional()
        .describe(
            '"static" renders the form inline on the page; "modal" renders a button that opens it in a ' +
                'dialog (the default for this widget).'
        ),
    modalTextHint: z.string().optional().describe('Hint shown inside the dialog — "modal" view only.'),
});

export type PartialPaymentFormWidgetData = z.infer<typeof partialPaymentFormWidgetDataSchema>;

/**
 * The keys this widget keeps on `data` itself. Handed to the shared fold rather than unioned into it,
 * so a `modalTitle` meant for a subscription form cannot land on a payment form — the two have
 * different modal-text fields and neither renderer reads the other's.
 */
export const PAYMENT_FORM_DATA_KEYS: readonly string[] = [
    'formView',
    'modalTextHint',
    'paymentFormName',
    'productName',
];
